import { useEffect, useState, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { Bell } from "lucide-react";
import { api } from "../lib/api";
import { logger } from "../lib/logger";
import { useI18n } from "../i18n";

function pointsBadgeClass(points) {
    if (points >= 5) return "bg-[var(--success-tint)] text-[var(--success)]";
    if (points > 0) return "bg-[var(--warning-tint)] text-[var(--warning)]";
    return "bg-[var(--overlay-light)] text-[var(--text-muted)]";
}

export default function NotificationBell() {
    const { t } = useI18n();
    const [open, setOpen] = useState(false);
    const [items, setItems] = useState([]);
    const [count, setCount] = useState(0);
    const ref = useRef(null);

    const fetchCount = useCallback(async () => {
        try {
            const { data } = await api.get("/notifications/unread-count");
            setCount(data.count || 0);
        } catch (e) {
            logger.warn("notifications: unread-count failed", e);
        }
    }, []);

    useEffect(() => {
        fetchCount();
        const id = setInterval(fetchCount, 30000);
        return () => clearInterval(id);
    }, [fetchCount]);

    useEffect(() => {
        const onDoc = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, []);

    const openMenu = async () => {
        const next = !open;
        setOpen(next);
        if (next) {
            try {
                const { data } = await api.get("/notifications");
                setItems(data);
                await api.post("/notifications/mark-read");
                setCount(0);
            } catch (e) {
                logger.warn("notifications: list/mark-read failed", e);
            }
        }
    };

    return (
        <div className="relative" ref={ref}>
            <button
                type="button"
                onClick={openMenu}
                data-testid="notification-bell"
                aria-label="Notifications"
                className="relative h-8 w-8 inline-flex items-center justify-center rounded-md border border-[var(--border-base)] bg-[var(--bg-card)] text-[var(--text-secondary)] hover:text-[var(--accent-hex)] hover:border-[var(--accent-border)] transition-colors"
            >
                <Bell className="h-3.5 w-3.5" />
                {count > 0 && (
                    <span
                        data-testid="notification-count"
                        className="absolute -top-1 -right-1 min-w-[1rem] h-4 px-1 rounded-full bg-[var(--danger)] text-white text-[9px] font-extrabold flex items-center justify-center"
                    >
                        {count > 9 ? "9+" : count}
                    </span>
                )}
            </button>

            {open && (
                <div
                    data-testid="notification-dropdown"
                    className="absolute right-0 top-10 w-80 max-h-[70vh] overflow-auto rounded-lg border border-[var(--border-base)] bg-[var(--bg-card)] shadow-2xl z-50"
                >
                    <div className="px-4 py-3 border-b border-[var(--border-muted)]">
                        <div className="font-heading text-sm font-bold uppercase tracking-widest text-[var(--text-primary)]">
                            {t("notif.title")}
                        </div>
                    </div>
                    {items.length === 0 ? (
                        <div className="px-4 py-8 text-center text-xs text-[var(--text-muted)]" data-testid="notif-empty">
                            {t("notif.empty")}
                        </div>
                    ) : (
                        <ul className="divide-y divide-[var(--border-muted)]">
                            {items.map((n) => (
                                <li key={n.id} className="px-4 py-3 hover:bg-[var(--overlay-light)]" data-testid={`notif-${n.id}`}>
                                    <Link to={`/pools/${n.pool_id}`} onClick={() => setOpen(false)} className="block">
                                        <div className="flex items-center justify-between gap-2">
                                            <div className="font-heading text-sm font-bold uppercase tracking-tight text-[var(--text-primary)] truncate">
                                                {n.title}
                                            </div>
                                            <span className={`text-[9px] font-extrabold uppercase tracking-widest px-1.5 py-0.5 rounded ${pointsBadgeClass(n.points || 0)}`}>+{n.points || 0}</span>
                                        </div>
                                        <div className="text-xs text-[var(--text-secondary)] mt-1">{n.message}</div>
                                        <div className="text-[10px] text-[var(--text-muted)] mt-1">{new Date(n.at).toLocaleString()}</div>
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            )}
        </div>
    );
}
